//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updatePreferredCaption } from '@apis/gatewayMediaApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import IItemMetadata from '@models/IItemMetadata';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { getNgRedux } from '@react-bridge/redux-bridge';
import { isDefined } from '@utils/filterPredicates';

export const getMetadataQueryKey = (cuid: Cuid) => [
  QueryKeyConstants.UniversalImageActions,
  QueryKeyConstants.UniversalImageActionsRights,
  QueryKeyConstants.UniversalImageActionsMetadata,
  cuid,
];

export interface UpdateCaptionMutationParams {
  caption: string;
  cuid: Cuid;
}

export const useUpdateCaptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    UpdateCaptionMutationParams,
    IItemMetadata
  >({
    mutationFn: async ({ caption, cuid }: UpdateCaptionMutationParams) => {
      await updatePreferredCaption(cuid, caption);
    },
    onMutate: async ({ caption, cuid }) => {
      const queryKey = getMetadataQueryKey(cuid);
      await queryClient.cancelQueries({ queryKey });
      const previousMetadata =
        queryClient.getQueryData<IItemMetadata>(queryKey);
      queryClient.setQueriesData(queryKey, (metadata?: IItemMetadata) => {
        if (isDefined(metadata)) {
          return {
            ...metadata,
            preferredCaption: caption,
          };
        }
      });
      return previousMetadata;
    },
    onError: (_, { cuid }, previousMetadata) => {
      queryClient.setQueryData(getMetadataQueryKey(cuid), previousMetadata);
      /**
       * We are unable to use Redux or the Angular injector due to the multiple Angular context issue.
       * TODO: PD-261610 - Migrate the UIA modal to React and remove the shared ngReduxInstance.
       */
      getNgRedux().dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Caption Update Failed',
          message: `Unable to update the caption. Please try again or contact Bazaarvoice Support.`,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSettled: (_, __, { cuid }) => {
      void queryClient.invalidateQueries(getMetadataQueryKey(cuid));
    },
  });
};
